/**
 * @file ENA Connection Application Page
 * Feature docs: https://www.notion.so/payaca/ENA-DNO-integration-106dc0fd6e60807299f8e1c6a059f096
 */

import useGetConnectionApplication from '@/api/queries/connectionApplication/useGetConnectionApplication';
import useGetConnectionApplicationSchema, {
  FormAndJSONSchema,
  FormSection,
} from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import { generateFormSectionsForScrollSpy } from '@/ui/components/enaForms/utils/display';
import ConnectionApplicationForm from '@/ui/pages/connectionApplicationPage/ConnectionApplicationForm';
import ConnectionApplicationSkeleton from '@/ui/pages/connectionApplicationPage/ConnectionApplicationSkeleton';
import { FC, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { UseFieldArrayReturn } from 'react-hook-form';
import { useParams } from 'react-router';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import {
  prepareFormSchema,
  prepareJSONSchema,
  RemovedFields,
} from '../../components/enaForms/utils/reactHookForm';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import ConnectionApplicationLayout from './ConnectionApplicationLayout';
import DnoResponseCard from './DnoResponseCard';

type UnionFieldArray = { fieldArray: UseFieldArrayReturn };

const ConnectionApplication: FC = () => {
  /**
   * Router
   */
  const { projectId, connectionApplicationId } = useParams<{
    projectId: string;
    connectionApplicationId: string;
  }>();

  /**
   * Queries
   */
  const { data: persistedFormStateData } = useGetConnectionApplication(
    connectionApplicationId
  );
  const { project } = useGetProjectByIdOverview(projectId);

  const lcts = useMemo(
    () => persistedFormStateData?.connectionApplication.data.lcts,
    [persistedFormStateData]
  );
  const { data: formSchema } = useGetConnectionApplicationSchema(lcts);

  return (
    <>
      <Helmet title={'DNO application'} />

      <AuthenticatedPageWrapper
        breadcrumbItems={[
          {
            label: `Projects`,
            to: `/deals`,
          },
          {
            label: `Project: #${project?.reference || ''}`,
            to: `/projects/${projectId}/property`,
          },
          {
            label: 'DNO application',
          },
        ]}
      >
        <div className="mx-auto max-w-[96rem] px-4">
          {formSchema && persistedFormStateData ? (
            <ConnectionForm formSchema={formSchema} />
          ) : (
            <ConnectionApplicationSkeleton />
          )}
        </div>
      </AuthenticatedPageWrapper>
    </>
  );
};

const ConnectionForm: FC<{
  formSchema: FormAndJSONSchema;
}> = (props) => {
  const { formSchema: unenhancedFormSchema } = props;

  /**
   * Router
   */
  const { connectionApplicationId } = useParams<{
    projectId: string;
    connectionApplicationId: string;
  }>();

  /**
   * Queries
   */
  const connectionApplication = useGetConnectionApplication(
    connectionApplicationId,
    false
  ).data?.connectionApplication;

  const dnoResponseScrollSection = {
    label: 'DNO Response',
    slug: 'dno-response',
    render: () => (
      <DnoResponseCard
        status={connectionApplication?.status}
        dnoComments={connectionApplication?.dnoComments}
        connectionApplicationStatus={
          connectionApplication?.connectionApplicationStatus
        }
      />
    ),
  };

  // This is 5k+ line JSON schema
  const [jsonSchema, formSchema] = useMemo(() => {
    const jsonSchema = prepareJSONSchema(unenhancedFormSchema, removedFields);
    const formSchema = prepareFormSchema(unenhancedFormSchema);

    // Remove "meterNumber", we use "mpan" to represent both MPAN and MPRN
    if (
      formSchema.properties?.installerCustomerDetails?.properties?.meterNumber
    ) {
      delete formSchema.properties.installerCustomerDetails.properties
        .meterNumber;
    }

    return [jsonSchema, formSchema];
  }, [unenhancedFormSchema]);

  const subSections = Object.entries<FormSection>(formSchema.properties).sort(
    ([_k, v], [_k2, v2]) => (v?.order ?? 0) - (v2?.order ?? 0)
  );

  const readonly = !!connectionApplication?.submittedAt;

  if (readonly) {
    return (
      <ConnectionApplicationLayout
        sections={[
          dnoResponseScrollSection,
          ...generateFormSectionsForScrollSpy({
            formSections: subSections,
            formSchema,
            formData: connectionApplication?.data,
            readonly: true,
          }),
        ]}
        connectionApplication={connectionApplication}
      />
    );
  }

  return (
    <ConnectionApplicationForm
      jsonSchema={jsonSchema}
      formSchema={formSchema}
      subSections={subSections}
    />
  );
};

// These sections can't be validated with Ajv so we remove, to manually write Zod schema for them.
const removedFields: RemovedFields = {
  // Can't be validated with Ajv so we remove, to manually write Zod schema for them.
  existingDevices: true,
  devicesToInstall: true,
  // We have a custom Zod step since we are not immediately uploading images to the server
  cutOutImages: true,
  additionalAttachments: true,
  installerCustomerDetails: {
    // Our address component returns a structured object of address properties, which we will later
    // flatten into these fields
    mainAddress: true,
    // We generate this from mainAddress
    installationPostCode: true,
    // We handle these in Zod because they can't be validated in Ajv
    mpan: true,
    meterNumber: true,
  },
  supplyDetails: {
    exportLimitDeviceDetails: true,
  },
};

export default ConnectionApplication;
